/**
 * The hero line's cycle (prototype v36/v38 `hcTick`): the words before the comma are backspaced and
 * retyped in front of a fixed "simplified.", one household's goals named by the money behind them.
 *
 * Durations are whole-phase, not per character: chained timers drift badly next to the canvas loop
 * (the prototype measured 3.4 s for a swap meant to take 1.5 s), so one rAF loop works out how many
 * characters should be showing from the elapsed time. Pure, so the timing is testable.
 */
export const HERO_CYCLE = {
  /** how long a whole phrase holds, ms */
  hold: 2000,
  /** backspacing the whole phrase, ms */
  del: 520,
  /** the empty pause between phrases, ms */
  gap: 180,
  /** typing the next phrase, ms */
  type: 760,
  /** the first phrase holds this much longer, while the story draws itself in, ms */
  intro: 2400,
  /** the back-off timer while the line can't be read, ms */
  idle: 400,
} as const;

export type HeroPhase = "hold" | "del" | "gap" | "type";
export type HeroCycleState = { i: number; phase: HeroPhase; t0: number };

/** The state at mount: the first phrase, held for `intro` + `hold`. */
export function heroCycleStart(now: number): HeroCycleState {
  return { i: 0, phase: "hold", t0: now + HERO_CYCLE.intro };
}

/**
 * One tick at time `now`. `ready` is whether the line can be read (story at the top, tab visible).
 * Returns the next state, the text to show and whether to back off to the idle timer: while it
 * can't be read the line parks on a whole phrase (never a broken word) and its clock restarts.
 * A swap already under way finishes on the rAF, as in the prototype.
 */
export function heroCycleTick(
  s: HeroCycleState,
  now: number,
  list: readonly string[],
  ready: boolean,
): { s: HeroCycleState; text: string; idle: boolean } {
  const from = list[s.i];
  const next = list[(s.i + 1) % list.length];
  const t = now - s.t0;
  switch (s.phase) {
    case "hold":
      if (!ready) return { s: { ...s, t0: now }, text: from, idle: true };
      if (t >= HERO_CYCLE.hold)
        return { s: { ...s, phase: "del", t0: now }, text: from, idle: false };
      return { s, text: from, idle: false };
    case "del":
      if (t >= HERO_CYCLE.del) return { s: { ...s, phase: "gap", t0: now }, text: "", idle: false };
      return {
        s,
        text: from.slice(0, Math.max(0, Math.ceil(from.length * (1 - t / HERO_CYCLE.del)))),
        idle: false,
      };
    case "gap":
      if (t >= HERO_CYCLE.gap)
        return { s: { ...s, phase: "type", t0: now }, text: "", idle: false };
      return { s, text: "", idle: false };
    case "type":
      if (t >= HERO_CYCLE.type) {
        return {
          s: { i: (s.i + 1) % list.length, phase: "hold", t0: now },
          text: next,
          idle: false,
        };
      }
      return {
        s,
        text: next.slice(
          0,
          Math.min(next.length, Math.floor((next.length * t) / HERO_CYCLE.type) + 1),
        ),
        idle: false,
      };
  }
}
